/**
 * 资源加载错误静默判定：第三方非核心脚本 + sourcemap（仅 .js.map / .css.map）
 * 命中则拦截，不重试、不上报。
 */

/** 可静默的第三方 host 片段（小写） */
export const SILENT_THIRD_PARTY_HOST_PARTS = [
    'clarity.ms',
    'connect.facebook.net',
    'googleads',
    'googletagmanager.com',
    'google',
    'analytics.tiktok.com',
    'tiktok.com'
] as const;

const SOURCE_MAP_SUFFIX = /\.(?:js|css)\.map(?:\?|#|$)/i;

/** google 相关 host（避免用裸 includes('google') 误伤） */
const isGoogleHost = (hostname: string) => {
    const host = hostname.toLowerCase();

    return (
        host === 'google.com' ||
        host.endsWith('.google.com') ||
        host === 'google.com.mx' ||
        host.endsWith('.google.com.mx') ||
        /(^|\.)googleadservices\.com$/.test(host) ||
        /(^|\.)googlesyndication\.com$/.test(host) ||
        /(^|\.)doubleclick\.net$/.test(host) ||
        /(^|\.)gstatic\.com$/.test(host) ||
        /(^|\.)googleapis\.com$/.test(host)
    );
};

/** 仅 .js.map / .css.map */
export const isSourceMapUrl = (url: string): boolean => {
    if (!url) return false;

    if (SOURCE_MAP_SUFFIX.test(url)) {
        return true;
    }

    try {
        const pathname = new URL(url, 'https://example.com').pathname;

        return /\.(?:js|css)\.map$/i.test(pathname);
    } catch {
        return false;
    }
};

export const isSilentThirdPartyUrl = (url: string): boolean => {
    if (!url) return false;

    try {
        const hostname = new URL(url, 'https://example.com').hostname.toLowerCase();

        if (isGoogleHost(hostname)) {
            return true;
        }

        if (
            hostname === 'clarity.ms' ||
            hostname.endsWith('.clarity.ms') ||
            hostname.includes('clarity.ms')
        ) {
            return true;
        }

        if (
            hostname === 'connect.facebook.net' ||
            hostname.endsWith('.facebook.net') ||
            hostname.includes('facebook.net')
        ) {
            return true;
        }

        if (hostname.includes('googleads') || hostname.includes('googletagmanager.com')) {
            return true;
        }

        // TikTok Pixel 等投放脚本（如 analytics.tiktok.com/i18n/pixel/events.js）
        if (hostname === 'tiktok.com' || hostname.endsWith('.tiktok.com')) {
            return true;
        }

        return false;
    } catch {
        const lower = url.toLowerCase();

        return (
            lower.includes('clarity.ms') ||
            lower.includes('facebook.net') ||
            lower.includes('googleads') ||
            lower.includes('googletagmanager.com') ||
            lower.includes('tiktok.com') ||
            /(?:^|[/.])google(?:\.com|ads|tagmanager|apis|syndication)/.test(lower)
        );
    }
};

/** 是否应按静默策略处理该资源 URL */
export const shouldSilentResourceUrl = (url: string): boolean => {
    if (!url) return false;

    return isSourceMapUrl(url) || isSilentThirdPartyUrl(url);
};

const resolveEventResourceUrl = (event: Event): string => {
    const target = event.target as
        | (EventTarget & { src?: string; href?: string })
        | null
        | undefined;

    if (!target || target === (window as unknown as EventTarget)) {
        return '';
    }

    return String(target.src || target.href || '');
};

/**
 * 捕获阶段拦截可静默资源错误，阻止后续 Omega / error-base 监听。
 * 返回卸载函数。
 */
export const installSilentResourceErrorGuard = (): (() => void) => {
    const onResourceError = (event: Event) => {
        const url = resolveEventResourceUrl(event);

        if (!url || !shouldSilentResourceUrl(url)) {
            return;
        }

        event.stopImmediatePropagation();

        if (typeof (event as ErrorEvent).preventDefault === 'function') {
            (event as ErrorEvent).preventDefault();
        }
    };

    window.addEventListener('error', onResourceError, true);

    return () => {
        window.removeEventListener('error', onResourceError, true);
    };
};

/**
 * 注入到 HTML（Omega 之前）的内联守卫脚本，保证先于 Omega 注册 capture 监听。
 * sourcemap 仅匹配 .js.map / .css.map；第三方含 clarity / facebook / googleads / gtm / google / tiktok。
 */
export const getSilentResourceErrorGuardInlineScript = (): string => {
    return '(function(){try{var mapRe=/\\.(?:js|css)\\.map(?:\\?|#|$)/i;function silent(u){if(!u)return false;if(mapRe.test(u))return true;try{var h=new URL(u,location.href).hostname.toLowerCase();if(h===\'google.com\'||h.slice(-11)===\'.google.com\'||h.indexOf(\'googleadservices.com\')>-1||h.indexOf(\'googlesyndication.com\')>-1||h.indexOf(\'doubleclick.net\')>-1||h.indexOf(\'gstatic.com\')>-1||h.indexOf(\'googleapis.com\')>-1)return true;if(h.indexOf(\'clarity.ms\')>-1||h.indexOf(\'facebook.net\')>-1||h.indexOf(\'googleads\')>-1||h.indexOf(\'googletagmanager.com\')>-1||h===\'tiktok.com\'||h.slice(-11)===\'.tiktok.com\')return true;}catch(e){var l=String(u).toLowerCase();if(l.indexOf(\'clarity.ms\')>-1||l.indexOf(\'facebook.net\')>-1||l.indexOf(\'googleads\')>-1||l.indexOf(\'googletagmanager.com\')>-1||l.indexOf(\'tiktok.com\')>-1||/\\.(?:js|css)\\.map(?:\\?|#|$)/i.test(l))return true;}return false;}window.addEventListener(\'error\',function(e){var t=e&&e.target;if(!t||t===window)return;var u=t.src||t.href||\'\';if(!silent(u))return;e.stopImmediatePropagation();if(e.preventDefault)e.preventDefault();},true);}catch(e){}})();';
};
